import React, {FC} from 'react';

import bgm1 from 'assets/v3/guide/video/bgm-1.webp';
import bgm2 from 'assets/v3/guide/video/bgm-2.webp';
import dubConfig from 'assets/v3/guide/video/dub-config.webp';
import editEntry from 'assets/v3/guide/video/edit-entry.webp';
import editSubtitle from 'assets/v3/guide/video/edit-subtitle.webp';
import exportImg from 'assets/v3/guide/video/export.webp';
import intro from 'assets/v3/guide/video/intro.webp';
import subtitleStyle1 from 'assets/v3/guide/video/subtitle-style-1.webp';
import subtitleStyle2 from 'assets/v3/guide/video/subtitle-style-2.webp';
import translating from 'assets/v3/guide/video/translating.webp';
import ttsConfig from 'assets/v3/guide/video/tts-config.webp';
import upload from 'assets/v3/guide/video/upload.webp';
import voiceToggle1 from 'assets/v3/guide/video/voice-toggle-1.webp';
import voiceToggle2 from 'assets/v3/guide/video/voice-toggle-2.webp';

/** Русский текст вкладки «Перевод видео» руководства (структура элементов 1:1 с zh.tsx). 2026-09-17 AI-перевод */
const RuContent: FC = () => {
  return (
    <>
      <h2>Обзор перевода видео</h2>
      <ul>
        <li>
          <strong>Перевод распознанной речи</strong>: поддерживается загрузка видео с чистой человеческой речью без
          субтитров (видео с исходными субтитрами тоже поддерживаются) — звук распознаётся и переводится на иностранный
          язык, а добавлять субтитры или нет — на ваше усмотрение.
        </li>
        <li>
          <strong>Перевод распознанных субтитров</strong>: поддерживается загрузка видео с исходными субтитрами. ИИ
          автоматически переводит субтитры, а затем озвучивает переведённые субтитры иностранным голосом.
        </li>
        <li>
          <strong>Ретушь текста</strong>: загрузите видео с текстом на экране — текст будет распознан и удалён.
        </li>
      </ul>
      <img alt="Обзор перевода видео" decoding="async" loading="lazy" src={intro} />

      <h2>Шаг 1: Загрузите видео</h2>
      <p>Разберём на примере перевода распознанной речи: выберите нужные языки и загрузите видео.</p>
      <img alt="Загрузка видео и выбор языков" decoding="async" loading="lazy" src={upload} />

      <h3>Настройки ИИ-озвучки</h3>
      <p>
        ИИ-озвучка включена по умолчанию. Нажмите на панель справа от опции озвучки, чтобы во всплывающем окне
        прослушать голоса и выбрать понравившийся.
      </p>
      <img alt="Настройки ИИ-озвучки" decoding="async" loading="lazy" src={dubConfig} />

      <h3>Фоновая музыка</h3>
      <p>
        При включении фоновой музыки вокал из исходной аудиодорожки по умолчанию удаляется — убирается закадровый голос,
        но сохраняются музыка и звуковые эффекты. Также можно выбрать трек из нашей фонотеки.
      </p>
      <img alt="Настройки фоновой музыки" decoding="async" loading="lazy" src={bgm1} />
      <img alt="Фонотека" decoding="async" loading="lazy" src={bgm2} />

      <h3>Настройки стиля субтитров</h3>
      <p>
        Включайте или выключайте субтитры по мере необходимости. При включении стиль и положение субтитров полностью
        настраиваются.
      </p>
      <img alt="Настройки стиля субтитров" decoding="async" loading="lazy" src={subtitleStyle1} />
      <img alt="Настройки положения субтитров" decoding="async" loading="lazy" src={subtitleStyle2} />

      <p>После завершения загрузки начинается перевод видео.</p>
      <img alt="Идёт перевод видео" decoding="async" loading="lazy" src={translating} />

      <h2>Шаг 2: Отредактируйте субтитры и озвучку</h2>
      <p>
        После перевода видео можно сразу просмотреть и скачать либо продолжить его редактирование. Нажмите кнопку
        【Редактировать】, чтобы открыть рабочую область перевода видео.
      </p>
      <img alt="Открытие рабочей области редактирования видео" decoding="async" loading="lazy" src={editEntry} />

      <p>
        Нажмите на любой субтитр или текст — справа появится всплывающее окно 【Редактирование субтитров】. В области
        редактирования текста можно изменить содержимое, шрифт, размер, цвет и другие параметры выбранного субтитра.
      </p>
      <img alt="Всплывающее окно редактирования субтитров" decoding="async" loading="lazy" src={editSubtitle} />

      <h3>Настройки озвучки</h3>
      <p>
        Нажмите 【Настройки озвучки】, чтобы настроить озвучивание субтитров. После изменения содержимого субтитров или
        настроек озвучки нажмите 【Предпросмотр】, чтобы заново сгенерировать обновлённое озвучивание и заменить
        автоматическую озвучку.
      </p>
      <img alt="Настройки озвучки" decoding="async" loading="lazy" src={ttsConfig} />

      <p>
        Если заголовок не нужно озвучивать или строка была пропущена, нажмите на этот субтитр, чтобы переключиться между
        озвученным субтитром и обычным текстом (переключаться можно свободно в обе стороны).
      </p>
      <img
        alt="Переключение между озвученным субтитром и обычным текстом"
        decoding="async"
        loading="lazy"
        src={voiceToggle1}
      />
      <img alt="Переключатель озвучки субтитров" decoding="async" loading="lazy" src={voiceToggle2} />

      <h2>Шаг 3: Экспортируйте видео</h2>
      <p>
        Когда всё готово, нажмите кнопку 【Экспорт】 в правом верхнем углу, выберите нужное качество и экспортируйте
        видео для публикации.
      </p>
      <img alt="Экспорт видео" decoding="async" loading="lazy" src={exportImg} />
    </>
  );
};

export default RuContent;
